/**
 * Create / edit a personal calendar event (crm.user_calendar_events) — used both
 * for one-off events and for blocking time, optionally on a weekly recurrence.
 *
 * Times are edited in the user's LOCAL timezone via native date / datetime-local
 * inputs and converted to ISO on save. All-day events span local midnight →
 * end-of-day. A weekly block stores the time-of-day + duration on the base row
 * plus the chosen weekdays; occurrences are expanded for display client-side.
 * Editing a recurring event edits the whole series. Owner-scoped on the backend
 * — this modal only ever touches the caller's own events.
 *
 * OUT OF OFFICE. The "Out of office (PTO)" button used to preset an all-day
 * blocking event and nothing else, which made only half of the promise true:
 * the block steers NEW leads toward available teammates, but the conversations,
 * callbacks, follow-ups and tasks you already hold sat in your queue and nobody
 * else's for the whole time you were away. So the same gesture now also files a
 * real absence (`crm.user_absences`, POST /api/crm/absences) and links it to
 * this event via `calendar_event_id` — the event stays on your own grid, and the
 * absence is the team-visible fact the coverage resolver reads. The event is
 * saved first so the absence can carry its id; if the absence write fails the
 * event is NOT re-created on retry.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Repeat, Trash2, UserMinus, X } from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { AbsenceKind, UserAbsence } from '../../types';
import { invalidateTeam } from '../../lib';
import { EVENT_COLORS, DEFAULT_EVENT_COLOR } from './colors';
import { ABSENCE_KINDS, backOnLabel } from './AbsenceModal';
import { Alert, Z } from '../ui';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const pad = (n: number) => String(n).padStart(2, '0');
const toDatetimeLocal = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

interface EventModalProps {
  /** Present → edit that event; absent → create. */
  eventId?: string | null;
  initialTitle?: string;
  initialStart: Date;
  initialEnd: Date;
  initialAllDay?: boolean;
  initialLocation?: string | null;
  initialDescription?: string | null;
  initialColor?: string | null;
  initialRecurrence?: 'weekly' | null;
  initialRecurrenceDays?: number[] | null;
  initialRecurrenceUntil?: string | null;
  initialBlocksBooking?: boolean;
  onClose: () => void;
  /** Parent refetches the calendar. */
  onSaved: () => void;
}

export default function EventModal({
  eventId,
  initialTitle = '',
  initialStart,
  initialEnd,
  initialAllDay = false,
  initialLocation = '',
  initialDescription = '',
  initialColor = DEFAULT_EVENT_COLOR,
  initialRecurrence = null,
  initialRecurrenceDays = null,
  initialRecurrenceUntil = null,
  initialBlocksBooking = true,
  onClose,
  onSaved,
}: EventModalProps) {
  const isEdit = !!eventId;
  const [title, setTitle] = useState(initialTitle);
  const [allDay, setAllDay] = useState(initialAllDay);
  const [blocksBooking, setBlocksBooking] = useState(initialBlocksBooking);
  const [start, setStart] = useState<Date>(initialStart);
  const [end, setEnd] = useState<Date>(initialEnd);
  const [location, setLocation] = useState(initialLocation || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [color, setColor] = useState(initialColor || DEFAULT_EVENT_COLOR);
  const [repeat, setRepeat] = useState<'none' | 'weekly'>(initialRecurrence === 'weekly' ? 'weekly' : 'none');
  const [recDays, setRecDays] = useState<number[]>(
    initialRecurrenceDays?.length ? [...initialRecurrenceDays] : [initialStart.getDay()]
  );
  const [recUntil, setRecUntil] = useState(initialRecurrenceUntil || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Out of office -------------------------------------------------------
  const { user } = useAuth();
  // The absence panel appears for any all-day block (that is what an OOO day
  // is); the PTO button ticks the box for you.
  const oooEligible = allDay && blocksBooking && repeat === 'none';
  const [fileAbsence, setFileAbsence] = useState(false);
  const [absenceKind, setAbsenceKind] = useState<AbsenceKind>('pto');
  /** An absence already covering these days — don't offer to file a second. */
  const [existingAbsence, setExistingAbsence] = useState<UserAbsence | null>(null);
  /** The day range the lookup below was run for, so widening the dates
   *  re-checks instead of trusting a stale answer. A REF, not state: this is
   *  read inside the lookup effect, and as state it sat in that effect's own
   *  dependency array — writing it re-ran the effect, React ran the PREVIOUS
   *  cleanup first, and the in-flight response was discarded as stale every
   *  single time, so `existingAbsence` could never become non-null. */
  const lookedUpRangeRef = useRef<string | null>(null);
  /** Set once the event itself is written, so a failed absence write retries
   *  ONLY the absence instead of creating a duplicate event. */
  const [savedEventId, setSavedEventId] = useState<string | null>(null);

  // Look up an existing absence over the edited days whenever the panel is
  // relevant and the range changes. Fails silent: worst case we offer a box
  // that files a second absence, which the resolver reads as the same "this
  // person is out".
  useEffect(() => {
    if (!oooEligible) return;
    const from = toDateInput(start);
    const to = toDateInput(end);
    const rangeKey = `${from}|${to}|${eventId || ''}|${user?.id || ''}`;
    if (lookedUpRangeRef.current === rangeKey) return;
    lookedUpRangeRef.current = rangeKey;
    // The answer on file is for the OLD range; drop it so the "already marked
    // out" banner can't linger over days it was never checked against.
    setExistingAbsence(null);
    let alive = true;
    api
      .get('/api/crm/absences', { params: { from, to } })
      .then((res) => {
        if (!alive) return;
        const rows = (res.data?.absences || []) as (UserAbsence & { calendar_event_id?: string | null })[];
        const hit = rows.find(
          (a) =>
            (eventId && a.calendar_event_id === eventId) ||
            (!!user?.id && a.user_id === user.id && a.starts_on <= to && a.ends_on >= from)
        );
        setExistingAbsence(hit || null);
      })
      .catch(() => {
        /* fail-safe: behave as if nothing is on file */
      });
    return () => {
      alive = false;
    };
  }, [oooEligible, start, end, eventId, user?.id]);

  // The "mark me out of office" checkbox lives INSIDE the `oooEligible` panel,
  // but the PTO button sets the flag directly. Un-ticking All-day, clearing
  // "make me unavailable", or choosing a weekly repeat takes the panel off the
  // screen while leaving the flag armed — saving would then file a full
  // out-of-office (and redistribute the person's whole day of work) with no
  // visible control anywhere in the modal saying so. Clear it with the panel,
  // and the write below is gated on the same condition.
  useEffect(() => {
    if (!oooEligible && fileAbsence) setFileAbsence(false);
  }, [oooEligible, fileAbsence]);

  const toggleDay = (d: number) =>
    setRecDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));

  const setRepeatMode = (mode: 'none' | 'weekly') => {
    setRepeat(mode);
    // Seed a sensible default weekday (the start day) when turning repeat on.
    if (mode === 'weekly' && recDays.length === 0) setRecDays([start.getDay()]);
  };

  const onTimedChange = (which: 'start' | 'end') => (v: string) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return;
    which === 'start' ? setStart(d) : setEnd(d);
  };
  const onDateChange = (which: 'start' | 'end') => (v: string) => {
    const d = new Date(`${v}T00:00`);
    if (Number.isNaN(d.getTime())) return;
    which === 'start' ? setStart(d) : setEnd(d);
  };

  /**
   * Build the {starts_at, ends_at} ISO pair from the local inputs.
   * NOTE: all-day events are stored as local-midnight → local-23:59 in the
   * timestamptz column, so the calendar day is stable within a single timezone
   * (the whole recruiter team is single-zone today). When the Google sync phase
   * lands, all-day handling should move to a true date so the day is invariant
   * across zones.
   */
  const computeRange = (): { starts_at: string; ends_at: string } | { error: string } => {
    if (allDay) {
      const s = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0);
      const e = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 0, 0);
      if (e.getTime() < s.getTime()) return { error: 'End day must be on or after the start day' };
      return { starts_at: s.toISOString(), ends_at: e.toISOString() };
    }
    if (end.getTime() < start.getTime()) return { error: 'End time must be at or after the start time' };
    return { starts_at: start.toISOString(), ends_at: end.toISOString() };
  };

  const save = async () => {
    setError(null);
    if (!title.trim()) {
      setError('Give the event a title');
      return;
    }
    const range = computeRange();
    if ('error' in range) {
      setError(range.error);
      return;
    }
    if (repeat === 'weekly' && recDays.length === 0) {
      setError('Pick at least one weekday to repeat on');
      return;
    }
    if (repeat === 'weekly' && recUntil && recUntil < toDateInput(start)) {
      setError('"Repeat until" must be on or after the start date');
      return;
    }
    const payload = {
      title: title.trim(),
      starts_at: range.starts_at,
      ends_at: range.ends_at,
      all_day: allDay,
      location: location.trim() || null,
      description: description.trim() || null,
      color,
      recurrence: repeat === 'weekly' ? 'weekly' : null,
      recurrence_days: repeat === 'weekly' ? [...recDays].sort((a, b) => a - b) : null,
      recurrence_until: repeat === 'weekly' ? recUntil || null : null,
      blocks_booking: blocksBooking,
    };
    setSaving(true);
    try {
      // The event first, so the absence can carry its id. `savedEventId` means
      // a previous attempt already wrote it and only the absence failed.
      let id = savedEventId;
      if (!id) {
        if (isEdit) {
          await api.patch(`/api/crm/calendar/events/${eventId}`, payload);
          id = eventId || null;
        } else {
          const res = await api.post('/api/crm/calendar/events', payload);
          id = (res.data?.event?.id as string) || null;
        }
        setSavedEventId(id);
      }

      // Then the absence — the half that actually redistributes the work.
      // `oooEligible` is part of the gate on purpose: it is the exact condition
      // the checkbox renders under, so we never file an absence the user cannot
      // currently see they asked for.
      if (oooEligible && fileAbsence && !existingAbsence) {
        try {
          await api.post('/api/crm/absences', {
            starts_on: toDateInput(start),
            ends_on: toDateInput(end),
            kind: absenceKind,
            note: null,
            ...(id ? { calendar_event_id: id } : {}),
          });
          // /api/crm/team is session-cached and now carries out_today — refresh
          // it or every owner picker keeps offering this person as available.
          invalidateTeam();
        } catch (absErr: any) {
          // The event is saved; be explicit that the sharing-out part is not.
          onSaved();
          setError(
            `Your ${isEdit ? 'change was' : 'event was'} saved, but marking you out of office failed — your work will not be shared out. ${errMsg(
              absErr
            )}`
          );
          setSaving(false);
          return;
        }
      }

      onSaved();
      onClose();
    } catch (e: any) {
      setError(errMsg(e));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!eventId) return;
    setError(null);
    setDeleting(true);
    try {
      await api.delete(`/api/crm/calendar/events/${eventId}`);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(errMsg(e));
      setDeleting(false);
    }
  };

  const busy = saving || deleting;
  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';
  const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

  return (
    <div className={`fixed inset-0 ${Z.modalRaised} flex items-center justify-center bg-black/50 p-4`} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit event' : 'New event'}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Edit event' : 'New event'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="evt-title">Title</label>
            <input
              id="evt-title"
              type="text"
              value={title}
              autoFocus
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Team huddle"
              className={inputCls}
            />
          </div>

          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              All day
            </label>
            <button
              type="button"
              onClick={() => {
                // PTO = an all-day block that takes you off bookings + lead
                // routing AND files a real absence, so the work you already
                // hold is shared out too (see the header comment).
                setAllDay(true);
                setBlocksBooking(true);
                setRepeatMode('none');
                setFileAbsence(true);
                if (!title.trim()) setTitle('Out of office (PTO)');
              }}
              className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Out of office (PTO)
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="evt-start">Starts</label>
              {allDay ? (
                <input id="evt-start" type="date" value={toDateInput(start)} onChange={(e) => onDateChange('start')(e.target.value)} className={inputCls} />
              ) : (
                <input id="evt-start" type="datetime-local" value={toDatetimeLocal(start)} onChange={(e) => onTimedChange('start')(e.target.value)} className={inputCls} />
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="evt-end">Ends</label>
              {allDay ? (
                <input id="evt-end" type="date" value={toDateInput(end)} onChange={(e) => onDateChange('end')(e.target.value)} className={inputCls} />
              ) : (
                <input id="evt-end" type="datetime-local" value={toDatetimeLocal(end)} onChange={(e) => onTimedChange('end')(e.target.value)} className={inputCls} />
              )}
            </div>
          </div>

          <div>
            <span className={`${labelCls} flex items-center gap-1`}>
              <Repeat className="h-3.5 w-3.5" /> Repeat
            </span>
            <select
              value={repeat}
              onChange={(e) => setRepeatMode(e.target.value as 'none' | 'weekly')}
              className={inputCls}
            >
              <option value="none">Does not repeat</option>
              <option value="weekly">Weekly on selected days</option>
            </select>
          </div>

          {repeat === 'weekly' && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
              <div>
                <span className={labelCls}>Repeat on</span>
                <div className="flex gap-1.5">
                  {WEEKDAY_INITIALS.map((wd, idx) => {
                    const on = recDays.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx)}
                        aria-pressed={on}
                        aria-label={WEEKDAY_NAMES[idx]}
                        title={WEEKDAY_NAMES[idx]}
                        className={`h-8 w-8 rounded-full text-xs font-semibold transition ${
                          on
                            ? 'bg-primary-600 text-white'
                            : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {wd}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="evt-until">
                  Repeat until <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="evt-until"
                  type="date"
                  value={recUntil}
                  min={toDateInput(start)}
                  onChange={(e) => setRecUntil(e.target.value)}
                  className={inputCls}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Leave empty to repeat indefinitely. The time above sets when each block starts and how long it lasts.
                </p>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={blocksBooking}
                onChange={(e) => setBlocksBooking(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                Make me unavailable during this time
                <span className="mt-0.5 block text-xs font-normal text-gray-400">
                  {blocksBooking
                    // Deliberately "go to" rather than "won’t be routed to you":
                    // crm.assign_study_recruiter falls back to the FULL roster when
                    // nobody on it is available, so a one-person study roster still
                    // receives leads while you are out (confirmed 2026-08-20 — never
                    // stranding a lead beats honouring the block). Promising otherwise
                    // was a claim the routing rule cannot keep.
                    ? 'Patients can’t book this time, and new leads go to available teammates on your studies while it’s active.'
                    : 'Shown as free — this event won’t affect your bookings or lead routing.'}
                </span>
              </span>
            </label>
          </div>

          {/* The other half of "out of office": what happens to the work you
              ALREADY hold. Blocking time alone never touched it. */}
          {oooEligible && (
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              {existingAbsence ? (
                <Alert tone="success" size="sm">
                  You’re already marked out of office for these days
                  {backOnLabel(existingAbsence.ends_on) ? ` (back ${backOnLabel(existingAbsence.ends_on)})` : ''} — your
                  conversations, callbacks, follow-ups and tasks are being shared out.
                </Alert>
              ) : (
                <>
                  <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={fileAbsence}
                      onChange={(e) => setFileAbsence(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="inline-flex items-center gap-1.5">
                        <UserMinus className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                        Mark me out of office these days
                      </span>
                      <span className="mt-0.5 block text-xs font-normal text-gray-400">
                        {fileAbsence
                          ? `Your conversations, callbacks, follow-ups and tasks for these days get shared out among the other recruiters on your studies who are in${
                              backOnLabel(toDateInput(end)) ? `, and come back ${backOnLabel(toDateInput(end))}` : ''
                            }. Your teammates can see that you’re away.`
                          : 'Blocking your time only steers NEW leads to available teammates — the leads, callbacks and tasks you already hold stay in your queue while you’re away.'}
                      </span>
                    </span>
                  </label>
                  {fileAbsence && (
                    <select
                      aria-label="Reason"
                      value={absenceKind}
                      onChange={(e) => setAbsenceKind(e.target.value as AbsenceKind)}
                      className={`mt-2 ${inputCls}`}
                    >
                      {ABSENCE_KINDS.map((k) => (
                        <option key={k.value} value={k.value}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className={labelCls} htmlFor="evt-location">Location <span className="text-gray-400">(optional)</span></label>
            <input id="evt-location" type="text" value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} placeholder="Room, address, or link" className={inputCls} />
          </div>

          <div>
            <label className={labelCls} htmlFor="evt-notes">Notes <span className="text-gray-400">(optional)</span></label>
            <textarea id="evt-notes" value={description} maxLength={2000} rows={2} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
          </div>

          <div>
            <span className={labelCls}>Color</span>
            <div className="flex flex-wrap gap-2">
              {EVENT_COLORS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setColor(c.key)}
                  aria-label={c.label}
                  aria-pressed={color === c.key}
                  className={`h-7 w-7 rounded-full ${c.dot} transition-transform ${
                    color === c.key ? 'ring-2 ring-offset-2 ring-primary-500 dark:ring-offset-gray-800' : 'opacity-80 hover:opacity-100'
                  }`}
                />
              ))}
            </div>
          </div>

          {isEdit && repeat === 'weekly' && (
            <p className="text-xs text-gray-500 dark:text-gray-400">Changes apply to the whole series.</p>
          )}

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          {isEdit ? (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
